/**
 * Rare equipment (04 §6): smoke grenades, night-vision goggles, wire cutters and the suppressed
 * pistol. It is found, never bought: a Supply Cache puts it in the operation's stock, `deploy`
 * takes what `Loadout.gear` names out of it, and what the ranger brings home goes back in.
 * Lost with a ranger who is killed or captured.
 */

import type { Gear, Outcome } from '../sim/types';
import { CACHE_FIND, GEAR, RARE_ITEMS, WEIGHT } from './content/economy';
import type { OperationState } from './state';
import type { SpecialItem } from './types';

export type Stock = Partial<Record<SpecialItem, number>>;

const NAMES: Record<SpecialItem, readonly [one: string, many: string]> = {
  smoke: ['smoke grenade', 'smoke grenades'],
  nvg: ['pair of night-vision goggles', 'pairs of night-vision goggles'],
  'wire-cutters': ['pair of wire cutters', 'pairs of wire cutters'],
  'suppressed-pistol': ['suppressed pistol', 'suppressed pistols'],
  ghillie: ['ghillie wrap', 'ghillie wraps'],
};

/** "2 smoke grenades", "1 suppressed pistol". */
export function countedName(item: SpecialItem, count: number): string {
  return `${count} ${NAMES[item][count === 1 ? 0 : 1]}`;
}

/** `Loadout.gear` as counts of stock items: smoke per grenade, the pistol as one item whatever its rounds. */
export function gearItems(gear: Partial<Gear> | undefined): Record<SpecialItem, number> {
  return {
    smoke: gear?.smoke ?? 0,
    nvg: gear?.nvg === true ? 1 : 0,
    'wire-cutters': gear?.wireCutters === true ? 1 : 0,
    'suppressed-pistol': (gear?.pistolRounds ?? 0) !== 0 ? 1 : 0,
    ghillie: gear?.ghillie === true ? 1 : 0,
  };
}

/** Only what is actually taken, so an empty `gear` leaves no trace on the deployment; undefined when nothing is. */
export function cleanGear(gear: Partial<Gear> | undefined): Partial<Gear> | undefined {
  const out: Partial<Gear> = {};
  if (gear?.smoke !== undefined && gear.smoke !== 0) out.smoke = gear.smoke;
  if (gear?.pistolRounds !== undefined && gear.pistolRounds !== 0) out.pistolRounds = gear.pistolRounds;
  if (gear?.nvg === true) out.nvg = true;
  if (gear?.wireCutters === true) out.wireCutters = true;
  if (gear?.ghillie === true) out.ghillie = true;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Carry units the gear adds to the ranger's load. */
export function gearWeight(gear: Partial<Gear> | undefined): number {
  const items = gearItems(gear);
  let units = 0;
  for (const item of RARE_ITEMS) units += Math.max(0, items[item]) * WEIGHT[item];
  return units;
}

/** Throws an Error the loadout screen can show when the gear is malformed or the stock cannot supply it. */
export function checkGear(gear: Partial<Gear> | undefined, stock: Stock): void {
  const smoke = gear?.smoke ?? 0;
  if (!Number.isInteger(smoke) || smoke < 0 || smoke > GEAR.maxSmoke) {
    throw new Error(`A ranger carries 0-${GEAR.maxSmoke} smoke grenades.`);
  }
  const rounds = gear?.pistolRounds ?? 0;
  if (rounds !== 0 && rounds !== GEAR.pistolRounds) {
    throw new Error(`The suppressed pistol is issued with exactly ${GEAR.pistolRounds} rounds.`);
  }
  const wanted = gearItems(gear);
  for (const item of RARE_ITEMS) {
    const have = stock[item] ?? 0;
    if (wanted[item] > have) {
      throw new Error(`The operation stock holds ${countedName(item, have)}; this loadout asks for ${wanted[item]}.`);
    }
  }
}

function addToStock(stock: Stock, item: SpecialItem, count: number): void {
  const left = (stock[item] ?? 0) + count;
  if (left > 0) stock[item] = left;
  else delete stock[item];
}

/** A Supply Cache's equipment choice, or a Quartermaster unlock's issue at the start of a run. */
export function stockFind(stock: Stock, item: SpecialItem): string {
  addToStock(stock, item, CACHE_FIND[item]);
  return countedName(item, CACHE_FIND[item]);
}

/** Every run starts with one find of each item the Quartermaster's ledger has unlocked (04 §5). */
export function startingStock(unlocks: readonly string[]): Stock {
  const stock: Stock = {};
  for (const item of RARE_ITEMS) if (unlocks.includes(`armory-${item}`)) stockFind(stock, item);
  return stock;
}

/** Validate and take: from here the gear is on the ranger, not in the stock. */
export function takeGear(op: OperationState, gear: Partial<Gear> | undefined): void {
  checkGear(gear, op.stock);
  const wanted = gearItems(gear);
  for (const item of RARE_ITEMS) if (wanted[item] > 0) addToStock(op.stock, item, -wanted[item]);
}

/**
 * What comes back after the mission. He never jumped: all of it. He got home (extracted, or
 * walked out): the goggles, cutters and pistol, and whatever smoke `left` says he still had -
 * none, when the sim did not say. Killed or captured: nothing. Never more than was taken.
 * Returns what was lost for good, as words for the log ('' when nothing was).
 */
export function returnGear(
  op: OperationState, taken: Partial<Gear> | undefined, outcome: Outcome, left: Partial<Gear> | undefined,
): string {
  const out = gearItems(taken);
  const home = outcome === 'extracted' || outcome === 'walkedOut';
  const smokeLeft = Math.floor(left?.smoke ?? 0);
  const back: Record<SpecialItem, number> = outcome === 'scrubbed' ? out : {
    smoke: home ? Math.min(out.smoke, Math.max(0, Number.isFinite(smokeLeft) ? smokeLeft : 0)) : 0,
    nvg: home && left?.nvg !== false ? out.nvg : 0,
    'wire-cutters': home && left?.wireCutters !== false ? out['wire-cutters'] : 0,
    'suppressed-pistol': home ? out['suppressed-pistol'] : 0,
    ghillie: home && left?.ghillie !== false ? out.ghillie : 0,
  };
  const lost: string[] = [];
  for (const item of RARE_ITEMS) {
    if (back[item] > 0) addToStock(op.stock, item, back[item]);
    // Smoke that was thrown is used, not lost: only what went down with the ranger is worth a line.
    const gone = out[item] - back[item];
    if (gone > 0 && (item !== 'smoke' || !home)) lost.push(countedName(item, gone));
  }
  return lost.join(', ');
}
